import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Callout } from '../blocks/Callout';
import { Figure } from '../blocks/Figure';
import { Screencast } from '../blocks/Screencast';
import { Step, Steps } from '../blocks/Steps';
import { YouTube } from '../blocks/YouTube';
import { createHeadingAnchors, type HeadingAnchors } from '../../lib/headingAnchor';
import { DOCS_NODE } from '../../model/docsNode.type';
import type {
  CalloutTone,
  FigureAttrs,
  ScreencastAttrs,
  StepAttrs,
  YoutubeAttrs,
} from '../../model/docsNode.type';
import { HEADING_LEVEL, type TiptapDoc, type TiptapNode } from '../../model/docs.type';
import { BODY_STYLE } from './docsBodyStyles';

/**
 * 저장된 본문(Tiptap JSON) → React.
 *
 * 게시판은 Tiptap 에디터를 `editable=false` 로 재사용해 본문을 그린다(PostViewer).
 * 도움말은 그렇게 하지 않는다 — 읽기만 하는 방문자 전원에게 ProseMirror 런타임을
 * 내려보내게 되고, 도움말은 읽기 성능과 검색 노출이 곧 제품이기 때문이다.
 * 대신 노드 스키마(`model/docsNode.type.ts`)를 에디터와 **공유**해 렌더가 갈라지지
 * 않게 한다. 에디터의 NodeView 도 여기 쓰는 것과 **같은 컴포넌트**를 쓴다.
 *
 * JSON 은 서버가 구조 검증 없이 저장하므로, 모르는 노드는 던지지 말고 **조용히
 * 건너뛴다**. 블록 하나가 이상해도 문서 전체가 흰 화면이 되면 안 된다.
 */
export function DocsBodyRenderer({ body }: { body: TiptapDoc | undefined }) {
  if (!body?.content?.length) return null;

  // 절 번호·앵커 id 는 문서 하나를 그리는 동안만 유효한 카운터로 만든다.
  return <>{renderNodes(body.content, createHeadingAnchors())}</>;
}

function renderNodes(nodes: TiptapNode[] | undefined, ctx: HeadingAnchors): ReactNode {
  if (!nodes) return null;
  return nodes.map((node, index) => (
    <Fragment key={index}>{renderNode(node, ctx)}</Fragment>
  ));
}

/** 노드 안의 순수 텍스트만 뽑는다. 코드 블록에 쓴다. */
function plainText(node: TiptapNode | undefined): string {
  if (!node) return '';
  if (node.text) return node.text;
  return (node.content ?? []).map(plainText).join('');
}

function attrsOf<T>(node: TiptapNode): Partial<T> {
  return (node.attrs ?? {}) as Partial<T>;
}

function renderNode(node: TiptapNode, ctx: HeadingAnchors): ReactNode {
  switch (node.type) {
    case 'text':
      return renderText(node);

    case 'hardBreak':
      return <br />;

    case 'paragraph':
      return <p className={BODY_STYLE.paragraph}>{renderNodes(node.content, ctx)}</p>;

    case 'heading':
      return renderHeading(node, ctx);

    case 'bulletList':
      return <ul className={BODY_STYLE.ul}>{renderNodes(node.content, ctx)}</ul>;

    case 'orderedList':
      return <ol className={BODY_STYLE.ol}>{renderNodes(node.content, ctx)}</ol>;

    case 'listItem':
      return <li>{renderNodes(node.content, ctx)}</li>;

    case 'blockquote':
      return (
        <blockquote className={BODY_STYLE.blockquote}>
          {renderNodes(node.content, ctx)}
        </blockquote>
      );

    case 'codeBlock':
      return (
        <pre className={BODY_STYLE.pre}>
          <code>{plainText(node)}</code>
        </pre>
      );

    case 'horizontalRule':
      return <hr className={BODY_STYLE.hr} />;

    case 'image':
      return renderImage(node);

    case 'table':
      return (
        <div className={BODY_STYLE.tableWrap}>
          <table className={BODY_STYLE.table}>
            <tbody>{renderNodes(node.content, ctx)}</tbody>
          </table>
        </div>
      );

    case 'tableRow':
      return <tr>{renderNodes(node.content, ctx)}</tr>;

    case 'tableHeader':
      return (
        <th className={BODY_STYLE.th} colSpan={numAttr(node, 'colspan')} rowSpan={numAttr(node, 'rowspan')}>
          {renderNodes(node.content, ctx)}
        </th>
      );

    case 'tableCell':
      return (
        <td className={BODY_STYLE.td} colSpan={numAttr(node, 'colspan')} rowSpan={numAttr(node, 'rowspan')}>
          {renderNodes(node.content, ctx)}
        </td>
      );

    // ── 도움말 전용 블록 ───────────────────────────────────
    case DOCS_NODE.CALLOUT: {
      const attrs = attrsOf<{ tone: CalloutTone; title: string | null }>(node);
      return (
        <Callout tone={attrs.tone ?? 'tip'} title={attrs.title ?? undefined}>
          {renderNodes(node.content, ctx)}
        </Callout>
      );
    }

    case DOCS_NODE.STEPS:
      return <Steps>{renderNodes(node.content, ctx)}</Steps>;

    case DOCS_NODE.STEP: {
      const attrs = attrsOf<StepAttrs>(node);
      return (
        <Step title={attrs.title ?? ''}>{renderNodes(node.content, ctx)}</Step>
      );
    }

    case DOCS_NODE.FIGURE: {
      const attrs = attrsOf<FigureAttrs>(node);
      if (!attrs.src) return null;
      return (
        <Figure
          src={attrs.src}
          alt={attrs.alt ?? ''}
          window={attrs.window ?? undefined}
        >
          {attrs.caption}
        </Figure>
      );
    }

    case DOCS_NODE.SCREENCAST: {
      const attrs = attrsOf<ScreencastAttrs>(node);
      if (!attrs.src) return null;
      return (
        <Screencast
          src={attrs.src}
          alt={attrs.alt ?? ''}
          poster={attrs.poster ?? undefined}
        >
          {attrs.caption}
        </Screencast>
      );
    }

    case DOCS_NODE.YOUTUBE: {
      const attrs = attrsOf<YoutubeAttrs>(node);
      if (!attrs.videoId) return null;
      return (
        <YouTube
          id={attrs.videoId}
          title={attrs.title ?? ''}
          description={attrs.description ?? undefined}
          duration={attrs.duration ?? undefined}
        />
      );
    }

    default:
      // 모르는 노드는 자식만 살려서 통과시킨다. 에디터에 블록이 추가되고 아직
      // 렌더러가 못 따라온 배포 창에서도 본문 텍스트는 읽힌다.
      return node.content ? <>{renderNodes(node.content, ctx)}</> : null;
  }
}

function numAttr(node: TiptapNode, key: string): number | undefined {
  const value = node.attrs?.[key];
  return typeof value === 'number' && value > 1 ? value : undefined;
}

function renderHeading(node: TiptapNode, ctx: HeadingAnchors): ReactNode {
  const level = node.attrs?.level === HEADING_LEVEL.SUB ? HEADING_LEVEL.SUB : HEADING_LEVEL.SECTION;
  const children = renderNodes(node.content, ctx);

  if (level === HEADING_LEVEL.SUB) {
    return (
      <h3 id={ctx.sub()} className={BODY_STYLE.h3}>
        <span data-docs-heading>{children}</span>
      </h3>
    );
  }

  const { number, id } = ctx.section();
  return (
    <h2 id={id} data-docs-number={number} className={BODY_STYLE.h2}>
      {/* 번호는 목차와 같은 값이다. 목차가 `data-docs-number` 를 읽으므로 따로 매기지 않는다. */}
      <span className="mr-2 tabular-nums text-brand-500 dark:text-brand-400">
        {number}.
      </span>
      {/* 목차는 이 span 만 읽는다. 앞의 번호·뒤의 앵커(#)가 목차 라벨에 섞이면 안 되는데,
          "첫 자식만 읽기"로 처리하면 굵게·코드로 시작하는 제목이 잘려나간다. */}
      <span data-docs-heading>{children}</span>
      <a
        href={`#${id}`}
        aria-label="이 절 링크"
        className="ml-2 text-[17px] font-normal text-neutral-300 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 dark:text-white/25"
      >
        #
      </a>
    </h2>
  );
}

/**
 * 본문 인라인 이미지(Tiptap Image). 캡처는 `<Figure>` 를 쓰고 이건 보조다.
 *
 * `width`/`height` 가 있으면 그대로 넘긴다 — 브라우저가 파일을 받기 전에 그 비율로
 * 자리를 잡아, lazy 로 늦게 뜨는 이미지가 아래 본문을 밀지 않는다. 앵커(`#s-30`)로
 * 이동한 위치가 이미지 로드 뒤에도 그대로인 이유다. 크기가 없는 옛 이미지는 예전처럼 그린다.
 */
function renderImage(node: TiptapNode): ReactNode {
  const src = typeof node.attrs?.src === 'string' ? node.attrs.src : '';
  if (!src) return null;
  const alt = typeof node.attrs?.alt === 'string' ? node.attrs.alt : '';

  return (
    <img
      src={src}
      alt={alt}
      width={positiveAttr(node, 'width')}
      height={positiveAttr(node, 'height')}
      loading="lazy"
      decoding="async"
      className={BODY_STYLE.image}
    />
  );
}

function positiveAttr(node: TiptapNode, key: string): number | undefined {
  const value = Number(node.attrs?.[key]);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * 본문에 들어와도 되는 링크 스킴.
 *
 * 본문 JSON 은 서버가 구조 검증 없이 저장한다(에디터 스키마와 이중 관리하지 않으려고).
 * 지금 작성자는 ADMIN 뿐이지만, 권한이 MANAGER 로 내려가는 순간 `javascript:` 한 줄이
 * 그대로 DOM 에 꽂히는 경로가 된다. 화이트리스트는 두 줄이라 미리 건다.
 */
const SAFE_LINK = /^(https?:|mailto:|tel:|\/|#)/i;

/** 텍스트 노드 + 마크. 링크는 내부/외부를 갈라 처리한다. */
function renderText(node: TiptapNode): ReactNode {
  let element: ReactNode = node.text ?? '';

  for (const mark of node.marks ?? []) {
    switch (mark.type) {
      case 'bold':
        element = <strong className="font-bold text-neutral-900 dark:text-neutral-100">{element}</strong>;
        break;
      case 'italic':
        element = <em>{element}</em>;
        break;
      case 'strike':
        element = <s>{element}</s>;
        break;
      case 'underline':
        element = <u>{element}</u>;
        break;
      case 'code':
        element = <code className={BODY_STYLE.code}>{element}</code>;
        break;
      case 'link': {
        const raw = typeof mark.attrs?.href === 'string' ? mark.attrs.href : '';
        const href = SAFE_LINK.test(raw) ? raw : '';
        if (!href) break; // 허용되지 않은 스킴 — 링크를 벗기고 글자만 남긴다.
        element = href.startsWith('/') ? (
          <Link to={href} className={BODY_STYLE.link}>
            {element}
          </Link>
        ) : (
          <a href={href} target="_blank" rel="noreferrer noopener" className={BODY_STYLE.link}>
            {element}
          </a>
        );
        break;
      }
      default:
        break;
    }
  }

  return element;
}
